import { useCallback, useState } from 'react';
import { ActivityIndicator, Pressable } from 'react-native';
import styled from 'styled-components/native';
import { REDESIGN_COLORS } from '../../styles/colors';

// --- Styled Components ---

const Container = styled.View`
  padding: 16px;
  background-color: ${REDESIGN_COLORS.cardBg};
  border-radius: 12px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const Title = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 15px;
  color: ${REDESIGN_COLORS.textPrimary};
  margin-bottom: 4px;
`;

const Description = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 13px;
  line-height: 20px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-bottom: 16px;
`;

const OptionButton = styled(Pressable)`
  flex-direction: row;
  align-items: center;
  padding: 14px 16px;
  border-radius: 8px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
  background-color: rgba(255, 255, 255, 0.05);
  margin-bottom: 8px;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const OptionIcon = styled.Text`
  font-size: 18px;
  margin-right: 12px;
`;

const OptionContent = styled.View`
  flex: 1;
`;

const OptionTitle = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 14px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

const OptionSubtitle = styled.Text`
  font-family: 'Satoshi-Regular';
  font-size: 12px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-top: 2px;
`;

const PhoneInfo = styled.View`
  margin-top: 8px;
  padding: 12px;
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border-width: 1px;
  border-color: ${REDESIGN_COLORS.borderSubtle};
`;

const PhoneLabel = styled.Text`
  font-family: 'Satoshi-Medium';
  font-size: 12px;
  color: ${REDESIGN_COLORS.textSecondary};
  margin-bottom: 4px;
`;

const PhoneNumber = styled.Text`
  font-family: 'Satoshi-Bold';
  font-size: 16px;
  color: ${REDESIGN_COLORS.textPrimary};
`;

/**
 * FollowUpPrompt - Shown when dealer has not replied after 48 hours.
 *
 * Provides two options:
 * 1. "Send follow-up email" - triggers follow-up draft generation
 * 2. "Try calling" - shows dealer phone (if available), triggers phone
 *    coaching guidance via generate_followup with channel: 'phone'
 *
 * @param {Object} props
 * @param {Object} props.negotiation - The negotiation object
 * @param {Function} props.onSendFollowUp - Called to trigger follow-up email
 * @param {Function} props.onTryCalling - Called to trigger phone coaching
 */
const FollowUpPrompt = ({ negotiation, onSendFollowUp, onTryCalling }) => {
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingPhone, setSendingPhone] = useState(false);
  const [showPhone, setShowPhone] = useState(false);

  const dealerPhone =
    negotiation?.listing_snapshot?.dealer?.phone ||
    negotiation?.listing_snapshot?.seller?.phone ||
    null;

  const handleSendFollowUp = useCallback(async () => {
    setSendingEmail(true);
    try {
      if (onSendFollowUp) {
        await onSendFollowUp();
      }
    } finally {
      setSendingEmail(false);
    }
  }, [onSendFollowUp]);

  const handleTryCalling = useCallback(async () => {
    setSendingPhone(true);
    setShowPhone(true);
    try {
      if (onTryCalling) {
        await onTryCalling();
      }
    } finally {
      setSendingPhone(false);
    }
  }, [onTryCalling]);

  return (
    <Container>
      <Title>No reply yet</Title>
      <Description>
        It has been a while since the dealer responded. Would you like to follow up?
      </Description>

      <OptionButton onPress={handleSendFollowUp} disabled={sendingEmail}>
        {sendingEmail ? (
          <ActivityIndicator
            size="small"
            color={REDESIGN_COLORS.textPrimary}
            style={{ marginRight: 12 }}
          />
        ) : (
          <OptionIcon>{'\u2709'}</OptionIcon>
        )}
        <OptionContent>
          <OptionTitle>Send follow-up email</OptionTitle>
          <OptionSubtitle>We will draft a polite nudge for you to review</OptionSubtitle>
        </OptionContent>
      </OptionButton>

      <OptionButton onPress={handleTryCalling} disabled={sendingPhone}>
        {sendingPhone ? (
          <ActivityIndicator
            size="small"
            color={REDESIGN_COLORS.textPrimary}
            style={{ marginRight: 12 }}
          />
        ) : (
          <OptionIcon>{'\u260E'}</OptionIcon>
        )}
        <OptionContent>
          <OptionTitle>Try calling</OptionTitle>
          <OptionSubtitle>
            {dealerPhone
              ? 'We will prepare talking points for you'
              : 'We will prepare talking points for you'}
          </OptionSubtitle>
        </OptionContent>
      </OptionButton>

      {showPhone && dealerPhone && (
        <PhoneInfo>
          <PhoneLabel>Dealer phone number</PhoneLabel>
          <PhoneNumber>{dealerPhone}</PhoneNumber>
        </PhoneInfo>
      )}
    </Container>
  );
};

export default FollowUpPrompt;
